import FilterDropdown from "@components/common/FilterDropdown.jsx";
import BusinessUnitsFilter from "@components/common/Filters/BusinessUnitsFilter.jsx";
import ClientsFilter from "@components/common/Filters/ClientsFilter.jsx";
import DistributorsFilter from "@components/common/Filters/DistributorsFilter.jsx";
import RolesFilter from "@components/common/Filters/RolesFilter.jsx";
import StaticSelectFilter from "@components/common/Filters/StaticSelectFilter.jsx";
import ModulesHeader from "@components/common/ModulesHeader";
import PageSizeSelector from "@components/common/PageSizeSelector.jsx";
import { truncateByDeviceTable } from "@components/mediaQuery/truncateByDevice";
import { useIsMobile, useIsTablet } from "@components/mediaQuery/useIsMobile";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useDropdownFilters } from "@hooks/useDropdownFilters.js";
import { fetchAllBusinessUnits } from "@services/accountMangement/businessUnitsService";
import { deleteUserById, fetchUsers } from "@services/accountMangement/userService.js";
import { setAllBusinessUnitsList } from "@store/modules/accountManagement/businessUnitsSlice.js";
import {
	setLoadingUsers,
	setUsersData,
	toggleUsersFiltersVisibility,
} from "@store/modules/accountManagement/usersSlice.js";
import FILTERS_KEYS from "@utils/filters_keys.js";
import { isInternalUser } from "@utils/isInternalUser";
import {
	IS_ACTIVATION_NAME,
	IS_ACTIVATION_ROLE,
	IS_ADMIN_NAME,
	IS_ADMIN_ROLE,
	IS_CUSTOMER_NAME,
	IS_CUSTOMER_READONLY_NAME,
	IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE,
	IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE_NAME,
	IS_DISTRIBUTOR_ROLE,
	IS_SALES_NAME,
} from "@utils/user_roles";
import { debounce } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/common/Button";
import ListTable from "../../../components/common/ListTable";
import Pagination from "../../../components/common/pagination";
import SearchInput from "../../../components/common/SearchInput";
import PlusIcon from "../../../components/icons/PlusIcon";
import { FLAGS } from "../../../config/growthbook/flags.js";

const statuses = [
	{ id: "0", status: "Disabled" },
	{ id: "1", status: "Enabled" },
];

const UsersList = () => {
	const { t } = useTranslation("users");
	const [searchTerms, setSearchTerms] = useState({
		name: "",
		id: "",
		role_id: "",
		role: "",
		email: "",
		status: "",
		businessUnits: "",
		clients: "",
		distributor: "",
		search: "",
	});
	const prevSearchTermsRef = useRef(searchTerms);

	const [selectedRole, setSelectedRole] = useState(null);
	const [client, setClient] = useState(null);
	const [distributor, setDistributor] = useState(null);
	const [businessUnit, setBusinessUnit] = useState(null);
	const [status, setStatus] = useState(null);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const deletion_message = "";

	const [currentPage, setCurrentPage] = useState(1);

	const { token, permissions, role, business_units } = useSelector((state) => state.auth.user);

	const isDistributor = [IS_DISTRIBUTOR_ROLE, IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE].includes(
		role?.id,
	);

	const filters = useSelector((state) => state.modules.accountManagement.userState.filters);

	const loading = useSelector((state) => state.modules.accountManagement.userState.loading);

	const perPage = useSelector((state) => state.modules.accountManagement.userState.perPage || 10);
	const totalPages = useSelector(
		(state) => state.modules.accountManagement.userState.totalPage || 1,
	);
	const totalItems = useSelector(
		(state) => state.modules.accountManagement.userState.totalItems || 0,
	);

	const [localPerPage, setLocalPerPage] = useState(perPage ?? 10);

	const usersList = useSelector((state) => state.modules.accountManagement.userState.data);

	const accountManagement = permissions.find(
		(resource) => resource.resource_name === "account_management",
	);
	const users = accountManagement?.sub_resources?.find(
		(subResource) => subResource.sub_resource_name === "account_management_users",
	);
	const authorizedPermissions = users?.permissions?.filter(
		(permission) => permission.authorized === true,
	);
	const canCreateUsers = authorizedPermissions?.some(
		(permission) => permission.name === "create users",
	);
	const canDeleteUsers = authorizedPermissions?.some(
		(permission) => permission.name === "delete users",
	);
	const canEditUsers = authorizedPermissions?.some(
		(permission) => permission.name === "update users",
	);
	const canViewUsers = authorizedPermissions?.some(
		(permission) => permission.name === "view users",
	);

	const distributors = accountManagement?.sub_resources?.find(
		(subResource) => subResource.sub_resource_name === "account_management_distributors",
	);
	const authorizedPermissionsDistributors = distributors?.permissions?.filter(
		(permission) => permission.authorized === true,
	);

	const canViewDistributors = authorizedPermissionsDistributors?.some(
		(permission) => permission.name === "view distributors",
	);

	const userrole = useSelector((state) => state.auth.user.role?.name);
	const userparentrole = useSelector((state) => state.auth.user.role?.parent_id);
	const userroleId = useSelector((state) => state.auth.user.role?.id);
	const internalUser = isInternalUser(role);

	const isRoleColumnVisible = useFeatureIsOn(FLAGS.users.list.roleColumnVisible);
	const isRolesFilterVisible = useFeatureIsOn(FLAGS.shared.filters.rolesFilterVisible);

	const handleRoleChange = (value) => {
		setSelectedRole(value);
		setSearchTerms((prev) => ({
			...prev,
			role_id: value?.id ?? null,
			role: value?.name ?? null,
		}));
	};

	const handleClientChange = (value) => {
		setClient(value);
		setSearchTerms((prev) => ({
			...prev,
			clients: [value?.id],
		}));
	};

	const handleDistributorChange = (value) => {
		setDistributor(value);
		setClient(null);
		setSearchTerms((prev) => ({
			...prev,
			distributor: [value?.id],
			clients: [],
		}));
	};

	const handleBusinessUnitChange = (value) => {
		setBusinessUnit(value);
		setDistributor(null);
		setClient(null);
		setSearchTerms((prev) => ({
			...prev,
			businessUnits: value?.id ? [value?.id] : [],
			distributor: [],
			clients: [],
		}));
	};

	const handleStatusChange = (value) => {
		setStatus(value);
		setSearchTerms((prev) => ({
			...prev,
			status: value?.id ?? null,
		}));
	};

	useEffect(() => {
		const searchTermsChanged =
			JSON.stringify(prevSearchTermsRef.current) !== JSON.stringify(searchTerms);

		// check for searchTerms and currentPage changes
		if (searchTermsChanged && currentPage !== 1) {
			prevSearchTermsRef.current = searchTerms;
			setCurrentPage(1);
			return;
		}

		prevSearchTermsRef.current = searchTerms;

		const controller = new AbortController();
		const debouncedFetch = debounce((sig) => {
			fetchUsersData(currentPage, sig, localPerPage);
		}, 300);

		debouncedFetch(controller.signal);

		return () => {
			debouncedFetch.cancel();
			controller.abort();
		};
	}, [currentPage, localPerPage, dispatch, token, navigate, searchTerms]);

	useEffect(() => {
		if (
			token &&
			// userrole !== IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE_NAME &&
			userrole !== IS_CUSTOMER_NAME &&
			userrole !== IS_CUSTOMER_READONLY_NAME
		) {
			const fetchBusinessUnits = async () => {
				try {
					const data = await fetchAllBusinessUnits(token, dispatch, navigate, 0);
					dispatch(setAllBusinessUnitsList({ data }));
				} catch (error) {
					console.error("Failed to fetch business units:", error);
				}
			};
			const debouncedBUFetch = debounce(() => {
				void fetchBusinessUnits();
			}, 300);

			debouncedBUFetch();

			return () => {
				debouncedBUFetch.cancel();
			};
		}
	}, [token, dispatch, navigate]);

	const fetchUsersData = async (pageToFetch = currentPage, signal, perPageOverride) => {
		dispatch(setLoadingUsers(true));
		try {
			const data = await fetchUsers(
				token,
				dispatch,
				navigate,
				pageToFetch,
				perPageOverride ?? localPerPage,
				1,
				searchTerms,
				signal,
			);
			if (pageToFetch > data.totalPage && data.totalPage > 0) {
				dispatch(setUsersData(data));
				fetchUsersData(data.totalPage);
			} else {
				dispatch(setUsersData(data));
			}
		} catch (error) {
			console.error("Error fetching users data:", error);
		} finally {
			dispatch(setLoadingUsers(false));
		}
	};

	const handlePageChange = (newPage) => {
		if (newPage !== currentPage && newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
		}
	};

	const startItemIndex = (currentPage - 1) * localPerPage + 1;
	const endItemIndex = Math.min(currentPage * localPerPage, totalItems);

	const statusColumn = {
		header: "Status",
		field: "status",
		content: (data) => (
			<span
				className={`rounded bg-gray-700 px-2.5 py-0.5 font-medium text-xs ${
					data.state === 1
						? "border border-green-400 text-green-400"
						: "border border-red-400 text-red-400"
				}`}
			>
				{data.state === 1 ? "Enabled" : "Disabled"}
			</span>
		),
	};
	const roleColumn = {
		header: "Role",
		field: "role",
		content: (rowData) =>
			rowData.role ? rowData.role.charAt(0).toUpperCase() + rowData.role.slice(1) : "N/A",
	};

	const creationDateColumn = {
		header: "Creation date",
		field: "creation_date",
		content: (rowData) => {
			const creationDateString = rowData.creation_date?.split(".")[0];
			const date = new Date(creationDateString);

			if (isNaN(date.getTime())) {
				return "Invalid Date";
			}

			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, "0");
			const day = String(date.getDate()).padStart(2, "0");
			const hours = String(date.getHours()).padStart(2, "0");
			const minutes = String(date.getMinutes()).padStart(2, "0");
			const seconds = String(date.getSeconds()).padStart(2, "0");

			return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
		},
	};

	let columns = [
		statusColumn,
		{ header: "Last name", field: "last_name" },
		{ header: "First name", field: "first_name" },
		{ header: "E-mail", field: "email" },
	];

	if (isDistributor) {
		//  Use GrowthBook feature flags to display role column
		if (isRoleColumnVisible) {
			columns.push(roleColumn);
		}
	} else {
		columns.push(
			roleColumn,
			{
				header: "Business Units",
				field: "business_units",
				content: (row) => {
					const resources = row.business_units;

					if (!resources || resources.length === 0) return "No Data";

					const buNames = resources.map((item) => item.country_code || item.name);

					const MAX_ITEMS = 1;
					const visible = buNames.slice(0, MAX_ITEMS).join(", ");
					const hiddenCount = buNames.length - MAX_ITEMS;

					return hiddenCount > 0 ? `${visible} +${hiddenCount} more` : visible;
				},
			},
			{
				header: "Clients",
				field: "clients",
				content: (row) => {
					const resources = row.clients;
					if (!resources || resources.length === 0) return "All Clients";

					const clientNames = resources.map((item) => item.name);
					const MAX_ITEMS = 1;
					const visible = clientNames.slice(0, MAX_ITEMS).join(", ");
					const hiddenCount = clientNames.length - MAX_ITEMS;

					// return hiddenCount > 0
					//   ? `${visible} +${hiddenCount} more`
					//   : visible;

					const displayText = hiddenCount > 0 ? `${visible} +${hiddenCount} more` : visible;

					return truncateByDeviceTable(displayText, isMobile, isTablet);
				},

				sortable: false,
			},

			{
				header: "Company",
				field: "company",
				content: (row) => {
					const resources = row?.company;
					if (!resources || resources.length === 0) return "null";

					const distNames = resources?.name;

					return truncateByDeviceTable(distNames, isMobile, isTablet);
				},

				sortable: false,
			},
		);
	}
	columns.push(creationDateColumn, { header: "Last log", field: "last_login" });

	if (userrole !== IS_ADMIN_NAME || userroleId !== IS_ADMIN_ROLE) {
		columns = columns.filter((col) => col?.field !== "clients" && col?.field !== "company");
	}

	const handleDeleteUsers = async (id) => {
		try {
			const result = await deleteUserById(id, token, dispatch, navigate, currentPage, perPage);
			if (result.status === "success") {
				setTimeout(() => {
					const current_page = usersList && usersList.length === 1 ? currentPage - 1 : currentPage;

					fetchUsersData(current_page);
				}, 1000);
				return true;
			}
			return false;
		} catch (error) {
			console.error("Error deleting user:", error);
			throw error;
		}
	};

	const handleSearchTermChange = (_, value) => {
		setSearchTerms((prevTerms) => {
			return {
				...prevTerms,
				search: value,
			};
		});
	};

	const onSearchTermClear = () => {
		setSearchTerms((prevTerms) => {
			return {
				...prevTerms,
				search: "",
			};
		});
	};

	const handleUserById = (id) => {
		if (canEditUsers) {
			navigate(`./edit/${id}`);
		} else null;
	};

	const isMobile = useIsMobile();
	const isTablet = useIsTablet();

	const canViewBusinessUnit =
		!isDistributor &&
		userrole !== IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE_NAME &&
		userrole !== IS_CUSTOMER_NAME;

	const canViewClients =
		userrole !== IS_DISTRIBUTOR_CUSTOMER_ADMIN_ROLE_NAME && userrole !== IS_CUSTOMER_NAME;

	const canViewDistributorsFilter =
		userrole === IS_ADMIN_NAME ||
		userrole === IS_ACTIVATION_NAME ||
		userrole === IS_SALES_NAME ||
		userparentrole === IS_ADMIN_ROLE ||
		userparentrole === IS_ACTIVATION_ROLE;

	const isFilterEnabled = (filterId) => {
		switch (filterId) {
			case FILTERS_KEYS.users.status:
				return true;
			//  Use GrowthBook feature flags to fetch role
			case FILTERS_KEYS.users.roles:
				return isRolesFilterVisible;
			case FILTERS_KEYS.users.clients:
				return canViewClients;
			case FILTERS_KEYS.users.distributors:
				return canViewDistributorsFilter;
			case FILTERS_KEYS.users.businessUnits:
				return business_units?.length !== 1;
			default:
				return false;
		}
	};

	const { dropdownFilters, isDropdownFilterVisible, toggleFilter, isFilterVisible } =
		useDropdownFilters({
			filters,
			isFilterEnabledFn: isFilterEnabled,
			toggleFiltersFn: toggleUsersFiltersVisibility,
		});
	return (
		<div className="px-0 pt-20 pb-4 sm:px-2 md:px-4 lg:px-4 xl:px-4 2xl:px-4">
			{isTablet && <ModulesHeader subModuleTitle="Users" />}

			<div className="w-full bg-navbar-bg p-6 shadow md:rounded-lg">
				<div className="flex justify-end md:justify-end">
					{canCreateUsers && (
						<Button
							text="Add New"
							icon={PlusIcon}
							onClick={() => navigate(`./create`)}
							className={"font-normal text-white"}
						/>
					)}
				</div>

				<form className="mt-4 flex flex-col items-start justify-start md:flex-row">
					{dropdownFilters && isDropdownFilterVisible && (
						<FilterDropdown filters={dropdownFilters} toggleFilter={toggleFilter} />
					)}
					<div className="mt-3 w-full flex-grow space-y-4 md:mt-0 md:grid md:grid-cols-2 md:gap-6 md:space-y-0 lg:grid-cols-3">
						<SearchInput
							id="search"
							dataTestId="users-search"
							placeholder={t("searchPlaceholder")}
							label={t("searchBy")}
							value={searchTerms?.search}
							onChange={handleSearchTermChange}
							onClear={onSearchTermClear}
						/>
						{/* filter by Role */}
						{isFilterVisible(FILTERS_KEYS.users.roles) && (
							<RolesFilter value={selectedRole} onChange={handleRoleChange} />
						)}
						{/* filter by Status */}
						{isFilterVisible(FILTERS_KEYS.users.status) && (
							<StaticSelectFilter
								id="status-filter"
								label="Filter by"
								placeholder="Status"
								value={status}
								onChange={handleStatusChange}
								options={statuses}
								optionLabel="status"
								optionValue="id"
								showSearch={false}
							/>
						)}
						{/* filter by Business Units */}
						{isFilterVisible(FILTERS_KEYS.users.businessUnits) && (
							<BusinessUnitsFilter
								value={businessUnit}
								onChange={handleBusinessUnitChange}
								enabled={business_units?.length !== 1}
							/>
						)}

						{canViewDistributorsFilter && isFilterVisible(FILTERS_KEYS.users.distributors) && (
							<DistributorsFilter
								value={distributor}
								onChange={handleDistributorChange}
								enabled={canViewDistributorsFilter}
								businessUnitId={businessUnit?.id}
							/>
						)}

						{/* filter by clients */}
						{canViewClients && isFilterVisible(FILTERS_KEYS.users.clients) && (
							<ClientsFilter
								value={client}
								onChange={handleClientChange}
								distributorId={distributor?.id}
								businessUnitId={businessUnit?.id}
								enabled={canViewClients}
							/>
						)}
					</div>
				</form>
			</div>
			{loading ? (
				<div className="flex h-64 items-center justify-center">
					<div className="h-32 w-32 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
				</div>
			) : (
				<div className="relative mt-4 overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
					<ListTable
						columns={columns}
						list={usersList}
						onDelete={handleDeleteUsers}
						onEdit={(id) => handleUserById(id)}
						handleRowClick={(id) => handleUserById(id)}
						message={deletion_message}
						editColumn={canEditUsers}
						deleteColumn={canDeleteUsers}
						isAction={!!canEditUsers || !!canDeleteUsers}
					/>
					<div className="flex flex-col items-center justify-between gap-2 bg-dark-blue bg-opacity-40 px-2 pt-4 pb-4 text-blue-gray text-xs sm:flex-row sm:gap-0 sm:px-4 sm:text-sm">
						<PageSizeSelector
							total={totalItems}
							value={localPerPage}
							startItem={startItemIndex}
							endItem={endItemIndex}
							onChange={(newSize) => {
								setLocalPerPage(newSize);
								setCurrentPage(1);
							}}
						/>
						<Pagination
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={handlePageChange}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default UsersList;
